import React from 'react';
import { useSelector } from 'react-redux';
import { Box } from '@material-ui/core'
import { createStyles, makeStyles } from '@material-ui/core/styles'
import { dateParse } from '@utilities/dateUtilities';
import {
	CaseDetailsAppointmentSelector,
	CaseDetailsInfoSelector,
	CaseDetailsPerformerSelector,
} from '@store/selectors/CaseDetailsSelector';
import { useCollectionEntity } from '@utilities/hooks/useCollectionEntity/useCollectionEntity';
import { Unit } from '@data/unit/Unit';
import { ENDPOINT_USER_UNITS } from '@utilities/apiConstants';
import { RootState } from '@interfaces/rootState';
import AppointmentDetailApi from '@interfaces/SurgeryRequest/AppointmentDetailApi';
import { PerformerApi } from '@interfaces/SurgeryRequest/PerformerApi';
import { SurgeryRequestGet } from '@interfaces/SurgeryRequest/SurgeryRequestGet';
import { Alert } from '@components/Alerts/Alert/Alert';
import { AlertDisplayField } from '@components/Alerts/AlertDisplayField/AlertDisplayField';
import useHasPermission from '@utilities/hooks/useHasPermission/useHasPermission';
import { PermittedAction } from '@utilities/PermittedAction';
import ConfirmCaseInput from './ConfirmCaseInput/ConfirmCaseInput';
import { NotificationTypeEnum } from '@interfaces/SurgeryRequest/NotificationTypeEnum';
import AlertDetails from '@components/Alerts/presentationComponents/AlertDetails';
import theme from '@theme/theme';

const useStyles = makeStyles((theme) => createStyles({
	alertDetails: {
		padding: theme.spacing(0),
	},
}));

export const UnscheduledAlert = () => {
	const classes = useStyles();
	const {
		unit,
		dateCreated,
		room
	} = useSelector(CaseDetailsInfoSelector as (state: RootState) => SurgeryRequestGet);
	const {
		procedureDate,
	} = useSelector(CaseDetailsAppointmentSelector as (state: RootState) => AppointmentDetailApi);
	const {
		firstName,
		lastName,
	} = useSelector(CaseDetailsPerformerSelector as (state: RootState) => PerformerApi);

	const [requestedAppointmentDate, requestedAppointmentTime, requestedAppointmentMeridiem] = React.useMemo(
		() => procedureDate ? dateParse(procedureDate) : [],
		[procedureDate]);

	const { hospitalName, name: unitName } = useCollectionEntity<Unit>(ENDPOINT_USER_UNITS, unit, 'id');

	const hasEnterFinPermission = useHasPermission(PermittedAction.ENTER_FIN);

	return (
		<Alert
			data-section="caseAlertBar"
			chipColor="warning"
			headerColor={`${theme.palette.warning[50]}`}
			chipText={NotificationTypeEnum.UNSCHEDULED.toLowerCase()}
			headerText="This procedure has not been scheduled."
			alertUser={firstName + ' ' + lastName}
			alertTime={dateCreated}
			body={
				<AlertDetails className={classes.alertDetails}>
					<AlertDisplayField
						label="Requested Surgery Date:"
					>
						{requestedAppointmentDate}
					</AlertDisplayField>
					<AlertDisplayField
						label="Requested Surgery Time:"
					>
						{requestedAppointmentTime && requestedAppointmentMeridiem
							? `${requestedAppointmentTime} ${requestedAppointmentMeridiem.toUpperCase()}`
							: '--'}
					</AlertDisplayField>
					<AlertDisplayField
						label="Appointment Location:"
					>
						{hospitalName} {unitName}
					</AlertDisplayField>
					<AlertDisplayField
						label="OR Room:"
					>
						{room ? room : '--'}
					</AlertDisplayField>
				</AlertDetails>
			}
			footer={hasEnterFinPermission &&
				<Box width="500px">
					<ConfirmCaseInput/>
				</Box>
			}
		/>
	);
};
